/**
 * Derive canonical (English) faction identity from a strategy for HUD colors, icons, and backgrounds.
 * Must use faction / faction_name_english — not faction_name, which is UI-localized.
 */

export const COMBINED_FACTIONS = {
  'The Conspiracy': ['League of Shadows', 'Asrah'],
  'The Corrupted Core': ['Exolot Dominion', 'Asrah'],
  'The Covenant': ['Ra\'s Children', 'League of Shadows'],
  'The Divine Syndicate': ['Ra\'s Children', 'Exolot Dominion'],
  'The Forsaken Pact': ['Remnants of Humanity', 'Asrah'],
  'The Hollow': ['Ra\'s Children', 'Asrah'],
  'The Iron Alliance': ['Remnants of Humanity', 'Exolot Dominion'],
  'The Last Crusade': ['Ra\'s Children', 'Remnants of Humanity'],
  'The Shadow Consortium': ['League of Shadows', 'Exolot Dominion'],
  'The Silent Accord': ['Remnants of Humanity', 'League of Shadows'],
};

/** Mono factions selectable in Strategy builder (display order). */
export const STRATEGY_MONO_FACTION_NAMES = [
  'Remnants of Humanity',
  'Exolot Dominion',
  'League of Shadows',
  'Ra\'s Children',
  'Asrah',
];

/** Profile avatar stems for Strategy mono-faction portraits. */
export const STRATEGY_MONO_FACTION_AVATARS = Object.freeze({
  'Remnants of Humanity': 'samuel_1',
  'Exolot Dominion': 'wengol_1',
  'League of Shadows': 'sulma_1',
  'Ra\'s Children': 'nehebkau_1',
  Asrah: 'harral_1',
});

/** Select SFX played when a mono faction is chosen in Strategy (League has none). */
export const STRATEGY_MONO_FACTION_SELECT_SOUNDS = Object.freeze({
  'Remnants of Humanity': '/sounds/samuel_select.mp3',
  'Exolot Dominion': '/sounds/wengol_select.mp3',
  'Ra\'s Children': '/sounds/nehebkau_select.mp3',
  Asrah: '/sounds/harral_select.mp3',
});

export const BASE_FACTION_NAMES = [
  'Asrah',
  'Exolot Dominion',
  'League of Shadows',
  'Ra\'s Children',
  'Remnants of Humanity',
];

const COMBINED_FACTION_NAMES = Object.keys(COMBINED_FACTIONS);

/**
 * Resolve the combined faction name for exactly two mono base factions.
 * @param {string} factionA
 * @param {string} factionB
 * @returns {string}
 */
export function getCombinedFactionName(factionA, factionB) {
  if (!factionA || !factionB) {
    throw new Error('getCombinedFactionName: both factionA and factionB are required');
  }
  if (factionA === factionB) {
    throw new Error(`getCombinedFactionName: factions must differ (got ${factionA})`);
  }
  const sorted = [factionA, factionB].sort();
  for (const [combinedName, pair] of Object.entries(COMBINED_FACTIONS)) {
    const sortedPair = [...pair].sort();
    if (sorted[0] === sortedPair[0] && sorted[1] === sortedPair[1]) {
      return combinedName;
    }
  }
  throw new Error(
    `getCombinedFactionName: no combined faction for pair [${factionA}, ${factionB}]`
  );
}

/**
 * Split a combined (or mono) faction identity into primary/secondary base slots.
 * @param {string} factionName
 * @returns {{ primaryFaction: string|null, secondaryFaction: string|null }}
 */
export function getBaseFactionsFromCombinedName(factionName) {
  if (!factionName || factionName === 'Unknown') {
    return { primaryFaction: null, secondaryFaction: null };
  }
  if (BASE_FACTION_NAMES.includes(factionName)) {
    return { primaryFaction: factionName, secondaryFaction: null };
  }
  const pair = COMBINED_FACTIONS[factionName];
  if (!pair) {
    throw new Error(`getBaseFactionsFromCombinedName: unknown faction "${factionName}"`);
  }
  // Preserve STRATEGY_MONO_FACTION_NAMES order for slot assignment.
  const ordered = STRATEGY_MONO_FACTION_NAMES.filter((f) => pair.includes(f));
  if (ordered.length !== 2) {
    throw new Error(
      `getBaseFactionsFromCombinedName: expected 2 mono bases for "${factionName}", got ${ordered.length}`
    );
  }
  return { primaryFaction: ordered[0], secondaryFaction: ordered[1] };
}

/**
 * Derive catalog/identity faction from dual Strategy slots.
 * @param {string|null} primaryFaction
 * @param {string|null} secondaryFaction
 * @returns {string|null}
 */
export function deriveSelectedFactionFromSlots(primaryFaction, secondaryFaction) {
  if (!primaryFaction) return null;
  if (!secondaryFaction) return primaryFaction;
  return getCombinedFactionName(primaryFaction, secondaryFaction);
}

const ALL_CANONICAL_FACTION_NAMES = [
  ...BASE_FACTION_NAMES,
  ...COMBINED_FACTION_NAMES,
  'The Sentients',
];

export function getOrderEnglishFactionName(order) {
  if (!order) {
    throw new Error('getOrderEnglishFactionName: order is required');
  }
  if (order.faction_name_english) {
    return order.faction_name_english;
  }
  if (order.faction) {
    return order.faction;
  }
  // getStrategyWithOrders stores canonical English in faction_name only (no separate faction field).
  if (order.faction_name && ALL_CANONICAL_FACTION_NAMES.includes(order.faction_name)) {
    return order.faction_name;
  }
  throw new Error(
    `getOrderEnglishFactionName: order ${order.id ?? '?'} missing English faction fields`
  );
}

export function getBaseFactionsFromStrategy(strategy) {
  if (!strategy || !strategy.orders || strategy.orders.length === 0) {
    return [];
  }

  const uniqueFactions = [
    ...new Set(strategy.orders.map((order) => getOrderEnglishFactionName(order))),
  ];

  const baseFactions = uniqueFactions
    .filter((f) => f && f.trim() !== '')
    .filter((f) => !COMBINED_FACTION_NAMES.includes(f))
    .filter((f) => BASE_FACTION_NAMES.includes(f) || f === 'The Sentients');

  return baseFactions.filter((f) => f !== 'The Sentients');
}

function isPureSentientsStrategy(strategy) {
  if (!strategy?.orders?.length) {
    return false;
  }
  return strategy.orders.every((order) => getOrderEnglishFactionName(order) === 'The Sentients');
}

export function calculateFactionFromStrategy(strategy) {
  if (!strategy || !strategy.orders || strategy.orders.length === 0) {
    return null;
  }

  const strategyEnglishFaction =
    strategy.faction_name_english ||
    strategy.faction ||
    (strategy.faction_name && ALL_CANONICAL_FACTION_NAMES.includes(strategy.faction_name)
      ? strategy.faction_name
      : null);
  if (strategyEnglishFaction) {
    return strategyEnglishFaction;
  }

  const baseFactionsOnly = getBaseFactionsFromStrategy(strategy);

  if (baseFactionsOnly.length === 0) {
    return isPureSentientsStrategy(strategy) ? 'The Sentients' : 'Unknown';
  }
  if (baseFactionsOnly.length === 1) {
    return baseFactionsOnly[0];
  }

  const sortedBaseFactions = [...baseFactionsOnly].sort();

  for (const [combinedFactionName, requiredFactions] of Object.entries(COMBINED_FACTIONS)) {
    const sortedRequired = [...requiredFactions].sort();
    const baseSet = new Set(sortedBaseFactions);
    const requiredSet = new Set(sortedRequired);

    if (
      baseSet.size === requiredSet.size &&
      sortedBaseFactions.every((f) => requiredSet.has(f)) &&
      sortedRequired.every((f) => baseSet.has(f))
    ) {
      return combinedFactionName;
    }
  }

  return baseFactionsOnly[0];
}
